import { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CategoryIcon from "@mui/icons-material/Category";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LinkIcon from "@mui/icons-material/Link";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import brokerAccountService from "@/services/brokerAccountService";
import marketOpportunityService from "@/services/marketOpportunityService";
import sectorClassificationService from "@/services/sectorClassificationService";
import stockMarketService from "@/services/stockMarketService";
import type {
  AdminOperationStatusRead,
  DhanInstrumentImportResponse,
  DhanInstrumentMasterAutoImportStatusResponse,
  EODImportStatusResponse,
  NseUniverseImportJobStatusResponse,
  SectorClassificationCoverageResponse,
  SectorClassificationStatusResponse,
  SectorClassificationSyncResult,
} from "@/types";
import { extractErrorMessage } from "@/utils/errors";
import VolumeObvScannerOperationsCard from "./VolumeObvScannerOperationsCard";
import ExecutionIntegrityCard from "./ExecutionIntegrityCard";

const EOD_STATUS_CHIP_COLOR: Record<string, "success" | "warning" | "error" | "default" | "info"> = {
  SUCCESS: "success",
  PARTIAL: "warning",
  FAILED: "error",
  RUNNING: "info",
  SKIPPED: "default",
  COMPLETED: "success",
  PENDING: "default",
};

/** Formats an ISO timestamp for the compact "last run" line next to each
 * section's caption — e.g. "19 Sep 2026, 20:45". `null`/`undefined` means
 * no run has happened yet, not an error. */
function formatLastRun(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

/** Formats the backend's 24h "HH:MM" scheduled clock time (e.g. "08:45",
 * mirrors STOCK_DAILY_IMPORT_TIME) into a 12-hour admin-friendly string,
 * e.g. "8:45 AM IST" — so the admin knows *when* the job runs, not just
 * whether it already has. Falls back to the raw string on unexpected
 * input rather than showing nothing. */
function formatScheduledTime(hhmm: string | null | undefined): string | null {
  if (!hhmm) return null;
  const [hourStr, minuteStr] = hhmm.split(":");
  const hour = Number(hourStr);
  const minute = Number(minuteStr);
  if (Number.isNaN(hour) || Number.isNaN(minute)) return hhmm;
  const period = hour >= 12 ? "PM" : "AM";
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${hour12}:${String(minute).padStart(2, "0")} ${period} IST`;
}

/**
 * Compact "last run" summary rendered next to a Data Operations section's
 * caption — a status Chip + a plain-English timestamp, so an admin can see
 * at a glance whether (and when) an action already ran, without opening
 * the detailed panel below. Lets an admin avoid re-running a
 * multi-minute/multi-hour action they can already see recently succeeded.
 */
function LastRunSummary({
  loading,
  status,
  timestamp,
}: {
  loading: boolean;
  status: string | null;
  timestamp: string | null | undefined;
}) {
  if (loading) {
    return (
      <Stack direction="row" spacing={0.75} alignItems="center">
        <CircularProgress size={12} />
        <Typography variant="caption" color="text.secondary">
          Checking last run…
        </Typography>
      </Stack>
    );
  }
  const formatted = formatLastRun(timestamp);
  if (!status || !formatted) {
    return (
      <Typography variant="caption" color="text.secondary">
        No run yet
      </Typography>
    );
  }
  return (
    <Stack direction="row" spacing={0.75} alignItems="center">
      <Chip size="small" label={status} color={EOD_STATUS_CHIP_COLOR[status] ?? "default"} />
      <Typography variant="caption" color="text.secondary">
        Last run: {formatted}
      </Typography>
    </Stack>
  );
}

/**
 * Administration > Data Operations — the 4 bulk maintenance actions that
 * used to live behind the "Admin Tools" button on the Stock Data page
 * toolbar (Resolve All Breeze Codes, Import All, Import & Score All NSE
 * Stocks, Recompute Indicators + Scores). Relocated here, unchanged, as
 * part of separating end-user analytics pages from back-office/ops
 * functions — see `MainLayout`'s "Administration" nav group.
 *
 * Every action below calls the exact same backend endpoints the old Stock
 * Data toolbar called (`stockMarketService`/`marketOpportunityService`) —
 * nothing on the backend changed, only where the trigger lives in the UI.
 * This page has no scanner table of its own to refresh afterward; visit
 * Stock Data / Market Opportunities to see the results.
 */
export function DataOperationsPage() {
  // ---- Resolve All Breeze Codes / Recompute Indicators + Scores ----
  const [busyAction, setBusyAction] = useState<"resolve" | "recompute" | null>(null);
  const [actionNotice, setActionNotice] = useState<string | null>(null);
  const [actionWarning, setActionWarning] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  /** Summarizes a `{symbol: reason}` failure map into one line so the admin
   * can see WHY nothing happened (e.g. an expired Breeze session token)
   * instead of just a silent "0 succeeded". Shows the first distinct
   * reason plus how many stocks hit it. */
  const summarizeFailures = (failed: Record<string, string>): string | null => {
    const entries = Object.entries(failed);
    if (entries.length === 0) return null;
    const [firstSymbol, firstReason] = entries[0];
    const suffix = entries.length > 1 ? ` (and ${entries.length - 1} more stock(s) with similar errors)` : "";
    return `${entries.length} stock(s) failed — e.g. ${firstSymbol}: ${firstReason}${suffix}`;
  };

  const runAction = async (
    kind: "resolve" | "recompute",
    action: () => Promise<{ notice: string; warning?: string | null }>
  ) => {
    setBusyAction(kind);
    setActionNotice(null);
    setActionWarning(null);
    setActionError(null);
    try {
      const result = await action();
      setActionNotice(result.notice);
      if (result.warning) setActionWarning(result.warning);
    } catch (err) {
      setActionError(extractErrorMessage(err));
    } finally {
      setBusyAction(null);
    }
  };

  const handleResolveAll = () =>
    runAction("resolve", async () => {
      const result = await stockMarketService.resolveAllBreezeCodes();
      return {
        notice: `Resolved ${result.resolved.length} stock(s)`,
        warning: summarizeFailures(result.failed),
      };
    });

  const handleRecomputeAll = () =>
    runAction("recompute", async () => {
      const result = await stockMarketService.recomputeAllActive();
      const succeeded = result.scanner_summary.processed.length;
      return {
        notice: `Recomputed indicators + scores for ${succeeded} stock(s)`,
        warning: summarizeFailures(result.scanner_summary.failed) ?? summarizeFailures(result.indicator_summary.failed),
      };
    });

  // ---- Import All (NIFTY 50, active + Breeze-resolved) ----
  const [importBusy, setImportBusy] = useState(false);
  const [importNotice, setImportNotice] = useState<string | null>(null);
  const [importWarning, setImportWarning] = useState<string | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() - 1);
    return d.toISOString().slice(0, 10);
  });
  const [toDate, setToDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [importAllStatus, setImportAllStatus] = useState<AdminOperationStatusRead | null>(null);
  const [importAllStatusLoading, setImportAllStatusLoading] = useState(true);

  const loadImportAllStatus = async () => {
    setImportAllStatusLoading(true);
    try {
      const status = await stockMarketService.getImportAllStatus();
      setImportAllStatus(status);
    } catch {
      // Best-effort header summary — the detailed Alert below already
      // surfaces real errors for the action itself; a failed status fetch
      // just leaves the compact summary showing "No run yet".
    } finally {
      setImportAllStatusLoading(false);
    }
  };

  useEffect(() => {
    loadImportAllStatus();
  }, []);

  const handleImportAll = async () => {
    setImportBusy(true);
    setImportNotice(null);
    setImportWarning(null);
    setImportError(null);
    try {
      const result = await stockMarketService.importAll({ from_date: fromDate, to_date: toDate });
      const failedRuns = result.import_runs.filter((r) => r.status === "FAILED");
      setImportNotice(`Imported ${result.import_runs.length} stock(s) and refreshed scores`);
      if (failedRuns.length > 0) {
        setImportWarning(
          `${failedRuns.length} of ${result.import_runs.length} import(s) failed — e.g. ${failedRuns[0].error_message ?? "unknown error"}`
        );
      }
    } catch (err) {
      setImportError(extractErrorMessage(err));
    } finally {
      setImportBusy(false);
      loadImportAllStatus(); // reflect this run's outcome in the header summary either way
    }
  };

  // ---- Import & Score All NSE Stocks (Phase 5 — full NSE universe, background job) ----
  const [nseFromDate, setNseFromDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 14);
    return d.toISOString().slice(0, 10);
  });
  const [nseToDate, setNseToDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [nseStarting, setNseStarting] = useState(false);
  const [nseError, setNseError] = useState<string | null>(null);
  const [nseJobStatus, setNseJobStatus] = useState<NseUniverseImportJobStatusResponse | null>(null);
  const nsePrevStateRef = useRef<string | null>(null);

  // Pick up any already-running job (e.g. started via the CLI script) as
  // soon as this page loads.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const status = await marketOpportunityService.getNseUniverseImportStatus();
        if (!cancelled) setNseJobStatus(status);
      } catch (err) {
        if (!cancelled) setNseError(extractErrorMessage(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Poll every 5s while a job is PENDING/RUNNING.
  useEffect(() => {
    if (!nseJobStatus || (nseJobStatus.state !== "PENDING" && nseJobStatus.state !== "RUNNING")) return;
    const timer = setInterval(async () => {
      try {
        const status = await marketOpportunityService.getNseUniverseImportStatus();
        setNseJobStatus(status);
      } catch (err) {
        setNseError(extractErrorMessage(err));
      }
    }, 5000);
    return () => clearInterval(timer);
  }, [nseJobStatus]);

  useEffect(() => {
    nsePrevStateRef.current = nseJobStatus?.state ?? null;
  }, [nseJobStatus]);

  const handleStartNseImport = async () => {
    setNseStarting(true);
    setNseError(null);
    try {
      const status = await marketOpportunityService.startNseUniverseImport({
        from_date: nseFromDate,
        to_date: nseToDate,
        interval: "1day",
      });
      setNseJobStatus(status);
    } catch (err) {
      setNseError(extractErrorMessage(err));
    } finally {
      setNseStarting(false);
    }
  };

  const nseJobRunning = nseJobStatus?.state === "PENDING" || nseJobStatus?.state === "RUNNING";

  // ---- Automated EOD Stock Data Update status (Automated EOD Stock Data
  // Foundation, 2026-09-13) — read-only, never triggers anything. Shows
  // whether STOCK_DAILY_IMPORT_ENABLED is on, and the last scheduled run's
  // trading date / stocks processed / Breakout calculation status /
  // failures, so an admin can see this without a DB query. ----
  const [eodStatus, setEodStatus] = useState<EODImportStatusResponse | null>(null);
  const [eodError, setEodError] = useState<string | null>(null);
  const [eodLoading, setEodLoading] = useState(true);

  const loadEodStatus = async () => {
    setEodLoading(true);
    setEodError(null);
    try {
      const status = await stockMarketService.getEodImportStatus();
      setEodStatus(status);
    } catch (err) {
      setEodError(extractErrorMessage(err));
    } finally {
      setEodLoading(false);
    }
  };

  useEffect(() => {
    loadEodStatus();
  }, []);

  // ---- Import Dhan Instrument Master (live-trading securityId resolution) ----
  const dhanFileInputRef = useRef<HTMLInputElement | null>(null);
  const [dhanFile, setDhanFile] = useState<File | null>(null);
  const [dhanUploading, setDhanUploading] = useState(false);
  const [dhanError, setDhanError] = useState<string | null>(null);
  const [dhanResult, setDhanResult] = useState<DhanInstrumentImportResponse | null>(null);
  const [dhanStatus, setDhanStatus] = useState<DhanInstrumentMasterAutoImportStatusResponse | null>(null);
  const [dhanStatusLoading, setDhanStatusLoading] = useState(true);

  const loadDhanStatus = async () => {
    setDhanStatusLoading(true);
    try {
      const status = await brokerAccountService.getDhanInstrumentMasterStatus();
      setDhanStatus(status);
    } catch {
      // Best-effort header summary — see loadImportAllStatus() above.
    } finally {
      setDhanStatusLoading(false);
    }
  };

  useEffect(() => {
    loadDhanStatus();
  }, []);

  const handleDhanUpload = async () => {
    if (!dhanFile) return;
    setDhanUploading(true);
    setDhanError(null);
    setDhanResult(null);
    try {
      const result = await brokerAccountService.importDhanInstrumentMaster(dhanFile);
      setDhanResult(result);
    } catch (err) {
      setDhanError(extractErrorMessage(err));
    } finally {
      setDhanUploading(false);
      loadDhanStatus(); // reflect this run's outcome in the header summary either way
    }
  };

  // ---- Sector/Industry Classification (centralized StockMaster sector
  // population — 2026-09-27, pivoted to manual CSV upload). Automated
  // fetch from NSE Indices was removed: niftyindices.com (Akamai-fronted)
  // silently tarpits non-browser requests in production (confirmed via a
  // live curl trace — full TLS handshake, then no HTTP response ever).
  // Same "Choose CSV file" + "Upload" pattern as "Import Dhan Instrument
  // Master" above; read-only coverage report fetched on mount and
  // re-fetched after a successful upload. ----
  const sectorFileInputRef = useRef<HTMLInputElement | null>(null);
  const [sectorFile, setSectorFile] = useState<File | null>(null);
  const [sectorUploading, setSectorUploading] = useState(false);
  const [sectorUploadError, setSectorUploadError] = useState<string | null>(null);
  const [sectorUploadResult, setSectorUploadResult] = useState<SectorClassificationSyncResult | null>(null);
  const [sectorCoverage, setSectorCoverage] = useState<SectorClassificationCoverageResponse | null>(null);
  const [sectorCoverageLoading, setSectorCoverageLoading] = useState(true);
  const [sectorCoverageError, setSectorCoverageError] = useState<string | null>(null);
  const [sectorStatus, setSectorStatus] = useState<SectorClassificationStatusResponse | null>(null);
  const [sectorStatusLoading, setSectorStatusLoading] = useState(true);

  const loadSectorCoverage = async () => {
    setSectorCoverageLoading(true);
    setSectorCoverageError(null);
    try {
      const coverage = await sectorClassificationService.getCoverage();
      setSectorCoverage(coverage);
    } catch (err) {
      setSectorCoverageError(extractErrorMessage(err));
    } finally {
      setSectorCoverageLoading(false);
    }
  };

  const loadSectorStatus = async () => {
    setSectorStatusLoading(true);
    try {
      const status = await sectorClassificationService.getStatus();
      setSectorStatus(status);
    } catch {
      // Best-effort header summary — see loadImportAllStatus() above.
    } finally {
      setSectorStatusLoading(false);
    }
  };

  useEffect(() => {
    loadSectorCoverage();
    loadSectorStatus();
  }, []);

  const handleSectorUpload = async () => {
    if (!sectorFile) return;
    setSectorUploading(true);
    setSectorUploadError(null);
    setSectorUploadResult(null);
    try {
      const result = await sectorClassificationService.uploadCsv(sectorFile);
      setSectorUploadResult(result);
      loadSectorCoverage(); // refresh coverage stats to show the upload's effect
    } catch (err) {
      setSectorUploadError(extractErrorMessage(err));
    } finally {
      setSectorUploading(false);
      loadSectorStatus(); // reflect this run's outcome in the header summary either way
    }
  };

  return (
    <Box>
      <Typography variant="h4" fontWeight={700} gutterBottom>
        Data Operations
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Bulk maintenance actions over the Stock Data / Market Scanner universe — resolving broker
        codes, fetching price history, and recomputing indicators/scores. These affect data shown on
        the Stock Data and Market Opportunities pages; visit those pages afterward to see results.
      </Typography>

      {actionError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}
      {actionWarning && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setActionWarning(null)}>
          {actionWarning}
        </Alert>
      )}
      {actionNotice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setActionNotice(null)}>
          {actionNotice}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Quick Actions
          </Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap" rowGap={2}>
            <Button
              variant="outlined"
              startIcon={busyAction === "resolve" ? <CircularProgress size={16} /> : <LinkIcon />}
              onClick={handleResolveAll}
              disabled={busyAction !== null}
            >
              Resolve All Breeze Codes
            </Button>
            <Button
              variant="outlined"
              startIcon={busyAction === "recompute" ? <CircularProgress size={16} /> : <RestartAltIcon />}
              onClick={handleRecomputeAll}
              disabled={busyAction !== null}
            >
              Recompute Indicators + Scores
            </Button>
          </Stack>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.5 }}>
            "Resolve All Breeze Codes" looks up each unresolved stock's Breeze security code so it
            can be imported. "Recompute Indicators + Scores" re-runs the Indicator Engine and Market
            Scanner over every active stock using data already stored — no new fetch from Breeze.
          </Typography>
        </CardContent>
      </Card>

      {/* Automated EOD Stock Data Update — read-only status for the new
          scheduled post-close import job (off by default). Nothing here
          triggers a run; visit this after the job has been enabled to
          confirm it ran. */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" rowGap={1} sx={{ mb: 1 }}>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" rowGap={0.5}>
              <Typography variant="h6">Automated EOD Stock Data Update</Typography>
              {eodStatus?.scheduled_time && (
                <Chip
                  size="small"
                  variant="outlined"
                  color={eodStatus.enabled ? "primary" : "default"}
                  label={`Scheduled: ${formatScheduledTime(eodStatus.scheduled_time)}`}
                />
              )}
              <LastRunSummary
                loading={eodLoading}
                status={eodStatus?.latest?.status ?? null}
                timestamp={eodStatus?.latest?.completed_at}
              />
            </Stack>
            <Button size="small" onClick={loadEodStatus} disabled={eodLoading}>
              {eodLoading ? <CircularProgress size={16} /> : "Refresh"}
            </Button>
          </Stack>

          {eodError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setEodError(null)}>
              {eodError}
            </Alert>
          )}

          {eodStatus && !eodStatus.enabled && (
            <Alert severity="info" variant="outlined">
              Not yet enabled (<code>STOCK_DAILY_IMPORT_ENABLED=false</code>) — daily stock OHLC data
              and Breakout Scores are still only updated by the manual actions on this page. Once
              enabled, it will run daily at {formatScheduledTime(eodStatus.scheduled_time)}. See the
              feature's delivery report for the steps to safely enable automated post-close imports.
            </Alert>
          )}

          {eodStatus && eodStatus.enabled && !eodStatus.latest && (
            <Alert severity="info" variant="outlined">
              Enabled, but no automated run has completed yet. Scheduled to run daily at{" "}
              {formatScheduledTime(eodStatus.scheduled_time)} — check back after that time, or come
              back tomorrow if today's run hasn't happened yet.
            </Alert>
          )}

          {eodStatus?.latest && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack spacing={1}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" rowGap={1}>
                  <Typography variant="subtitle2" fontWeight={700}>
                    Trading date: {eodStatus.latest.trading_date}
                  </Typography>
                  <Chip
                    size="small"
                    label={eodStatus.latest.status}
                    color={EOD_STATUS_CHIP_COLOR[eodStatus.latest.status] ?? "default"}
                  />
                </Stack>
                <Typography variant="body2">
                  Stocks processed: {eodStatus.latest.stocks_attempted} attempted —{" "}
                  {eodStatus.latest.stocks_succeeded} succeeded, {eodStatus.latest.stocks_failed} failed
                </Typography>
                <Typography variant="body2">
                  Breakout Score: {eodStatus.latest.breakout_calculated} calculated,{" "}
                  {eodStatus.latest.breakout_skipped} skipped
                </Typography>
                {eodStatus.latest.completed_at && (
                  <Typography variant="caption" color="text.secondary">
                    Completed at {new Date(eodStatus.latest.completed_at).toLocaleString()}
                  </Typography>
                )}
                {eodStatus.latest.skip_reason && (
                  <Typography variant="caption" color="text.secondary">
                    Skip reason: {eodStatus.latest.skip_reason}
                  </Typography>
                )}
                {eodStatus.latest.failure_details && eodStatus.latest.failure_details.length > 0 && (
                  <Box sx={{ maxHeight: 160, overflow: "auto" }}>
                    <Typography variant="caption" color="text.secondary">
                      {eodStatus.latest.failure_details.length} failure(s) — e.g.{" "}
                      {eodStatus.latest.failure_details[0].symbol}:{" "}
                      {eodStatus.latest.failure_details[0].reason}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Paper>
          )}
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" rowGap={0.5} sx={{ mb: 1 }}>
            <Typography variant="h6">Import All (NIFTY 50, active + resolved)</Typography>
            <LastRunSummary
              loading={importAllStatusLoading}
              status={importAllStatus?.status ?? null}
              timestamp={importAllStatus?.completed_at}
            />
          </Stack>
          {importError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setImportError(null)}>
              {importError}
            </Alert>
          )}
          {importWarning && (
            <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setImportWarning(null)}>
              {importWarning}
            </Alert>
          )}
          {importNotice && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setImportNotice(null)}>
              {importNotice}
            </Alert>
          )}
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={6} sm={3}>
              <TextField
                label="From date"
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                label="To date"
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <Button
                variant="contained"
                startIcon={importBusy ? <CircularProgress size={16} color="inherit" /> : <CloudDownloadIcon />}
                onClick={handleImportAll}
                disabled={importBusy}
              >
                {importBusy ? "Importing…" : "Import All"}
              </Button>
            </Grid>
          </Grid>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.5 }}>
            Fetches OHLC history from Breeze for every active, Breeze-resolved stock, then
            automatically runs the Indicator Engine and Market Scanner over the whole universe.
          </Typography>
        </CardContent>
      </Card>

      {/* NSE-universe bulk import/score — a SEPARATE, broader action from
          "Import All"/"Recompute All" above: this one covers EVERY NSE
          stock, not just the active NIFTY 50, and runs as a polled
          background job (can also be triggered from the CLI via
          scripts/import_score_all_nse_stocks.sh). */}
      <Card>
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" rowGap={0.5} sx={{ mb: 1 }}>
            <Typography variant="h6">Import &amp; Score All NSE Stocks</Typography>
            {nseJobStatus?.auto_import_scheduled_time && (
              <Chip
                size="small"
                variant="outlined"
                color={nseJobStatus.auto_import_enabled ? "primary" : "default"}
                label={
                  nseJobStatus.auto_import_enabled
                    ? `Auto-import: ${formatScheduledTime(nseJobStatus.auto_import_scheduled_time)}`
                    : "Auto-import: off"
                }
              />
            )}
            <LastRunSummary
              loading={false}
              status={nseJobStatus?.state ?? null}
              timestamp={nseJobStatus?.completed_at ?? nseJobStatus?.started_at}
            />
            {nseJobStatus?.triggered_by && (
              <Typography variant="caption" color="text.secondary">
                ({nseJobStatus.triggered_by === "scheduler" ? "automated" : "manual"})
              </Typography>
            )}
            {nseJobStatus?.source === "PERSISTED" && (
              <Typography variant="caption" color="text.secondary" fontStyle="italic">
                (from before the last backend restart)
              </Typography>
            )}
          </Stack>
          <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
            Covers the FULL NSE universe (thousands of stocks), not just the NIFTY 50 — fetches OHLC
            history from Breeze for every seeded NSE stock, then runs the Indicator Engine and Market
            Scanner over all of them. Runs in the background; this can take a while for a full-year
            range.{" "}
            {nseJobStatus?.auto_import_enabled && (
              <>
                Also runs automatically every day at{" "}
                {formatScheduledTime(nseJobStatus.auto_import_scheduled_time)}, re-fetching only the
                last few trading days (not a full year) to stay light — use the manual controls below
                for a wider date range or to pick up same-day changes immediately.
              </>
            )}
          </Alert>

          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <TextField
                label="From date"
                type="date"
                value={nseFromDate}
                onChange={(e) => setNseFromDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                disabled={nseJobRunning}
                fullWidth
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                label="To date"
                type="date"
                value={nseToDate}
                onChange={(e) => setNseToDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                disabled={nseJobRunning}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <Button
                variant="contained"
                startIcon={<TravelExploreIcon />}
                onClick={handleStartNseImport}
                disabled={nseStarting || nseJobRunning}
              >
                {nseJobRunning ? "Running…" : "Start Import & Score"}
              </Button>
            </Grid>
          </Grid>

          {nseError && (
            <Alert severity="error" sx={{ mt: 2 }} onClose={() => setNseError(null)}>
              {nseError}
            </Alert>
          )}

          {nseJobStatus && (
            <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
              <Stack spacing={1}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="subtitle2" fontWeight={700}>
                    Job status: {nseJobStatus.state}
                  </Typography>
                  {nseJobRunning && <CircularProgress size={18} />}
                </Stack>
                {nseJobStatus.total_stocks > 0 && (
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(100, (nseJobStatus.processed / nseJobStatus.total_stocks) * 100)}
                  />
                )}
                <Typography variant="body2">
                  Processed {nseJobStatus.processed} / {nseJobStatus.total_stocks} — succeeded{" "}
                  {nseJobStatus.succeeded}, failed {nseJobStatus.failed}
                </Typography>
                {nseJobStatus.current_symbol && nseJobRunning && (
                  <Typography variant="caption" color="text.secondary">
                    Current: {nseJobStatus.current_symbol}
                  </Typography>
                )}
                {nseJobStatus.state === "COMPLETED" && (
                  <Alert severity="success" sx={{ mt: 1 }}>
                    Completed — {nseJobStatus.succeeded} succeeded, {nseJobStatus.failed} failed.
                  </Alert>
                )}
                {nseJobStatus.state === "FAILED" && <Alert severity="error" sx={{ mt: 1 }}>Job failed.</Alert>}
                {nseJobStatus.errors.length > 0 && (
                  <Box sx={{ maxHeight: 160, overflow: "auto" }}>
                    <Typography variant="caption" color="text.secondary">
                      {nseJobStatus.errors.length} error(s) — e.g. {nseJobStatus.errors[0].symbol}:{" "}
                      {nseJobStatus.errors[0].error}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Paper>
          )}
        </CardContent>
      </Card>

      {/* Dhan securityId resolution — separate from the NSE/stock-data
          imports above: this feeds Live Trade Execution (Broker Accounts),
          not the Stock Data / Market Opportunities scanner universe. */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" rowGap={0.5} sx={{ mb: 1 }}>
            <Typography variant="h6">Import Dhan Instrument Master</Typography>
            {dhanStatus?.scheduled_time && (
              <Chip
                size="small"
                variant="outlined"
                color={dhanStatus.enabled ? "primary" : "default"}
                label={
                  dhanStatus.enabled
                    ? `Auto-import: ${formatScheduledTime(dhanStatus.scheduled_time)}`
                    : "Auto-import: off"
                }
              />
            )}
            <LastRunSummary
              loading={dhanStatusLoading}
              status={dhanStatus?.latest?.status ?? null}
              timestamp={dhanStatus?.latest?.completed_at}
            />
            {dhanStatus?.latest?.summary?.triggered_by && (
              <Typography variant="caption" color="text.secondary">
                ({dhanStatus.latest.summary.triggered_by === "scheduler" ? "automated" : "manual"})
              </Typography>
            )}
          </Stack>
          <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
            Dhan's order API needs a numeric securityId for every contract, which only Dhan's own
            published instrument master file provides.{" "}
            {dhanStatus?.enabled ? (
              <>
                This is downloaded and imported automatically every day at{" "}
                {formatScheduledTime(dhanStatus.scheduled_time)} from{" "}
                <code>https://images.dhan.co/api-data/api-scrip-master-detailed.csv</code> — you
                only need to use the manual upload below if you want to pick up a same-day change
                (e.g. a new expiry Dhan just listed) before the next scheduled run.
              </>
            ) : (
              <>
                Download the DETAILED scrip master from{" "}
                <code>https://images.dhan.co/api-data/api-scrip-master-detailed.csv</code> yourself,
                then upload it here.
              </>
            )}{" "}
            Only index-option (NIFTY/BANKNIFTY) rows are kept. Re-run this whenever Dhan lists new
            expiries — it never touches your other broker connections.
          </Alert>

          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" rowGap={2}>
            <Button variant="outlined" component="label" htmlFor="dhan-instrument-master-file">
              Choose CSV file
              <input
                id="dhan-instrument-master-file"
                ref={dhanFileInputRef}
                type="file"
                accept=".csv"
                hidden
                onChange={(e) => setDhanFile(e.target.files?.[0] ?? null)}
              />
            </Button>
            {dhanFile && (
              <Typography variant="body2" color="text.secondary">
                {dhanFile.name}
              </Typography>
            )}
            <Button
              variant="contained"
              startIcon={dhanUploading ? <CircularProgress size={16} color="inherit" /> : <UploadFileIcon />}
              onClick={handleDhanUpload}
              disabled={!dhanFile || dhanUploading}
            >
              {dhanUploading ? "Importing…" : "Import"}
            </Button>
          </Stack>

          {dhanError && (
            <Alert severity="error" sx={{ mt: 2 }} onClose={() => setDhanError(null)}>
              {dhanError}
            </Alert>
          )}

          {dhanResult && (
            <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
              <Stack spacing={1}>
                <Typography variant="body2">
                  {dhanResult.total_rows} row(s) read, {dhanResult.in_scope_rows} index-option row(s)
                  in scope — {dhanResult.imported} added, {dhanResult.updated} updated.
                </Typography>
                {dhanResult.unmatched.length > 0 && (
                  <Box sx={{ maxHeight: 160, overflow: "auto" }}>
                    <Typography variant="caption" color="text.secondary">
                      {dhanResult.unmatched.length} row(s) could not be parsed — e.g. security_id{" "}
                      {dhanResult.unmatched[0].raw_security_id}: {dhanResult.unmatched[0].reason}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Paper>
          )}
        </CardContent>
      </Card>

      {/* Sector/Industry Classification — centralized StockMaster.sector /
          .industry population, so Breakout Scanner, RSI2 Pullback, and the
          Stock Data page all read a consistent value instead of each
          deriving it independently. Manual CSV upload only (2026-09-27) —
          niftyindices.com (Akamai-fronted) silently tarpits non-browser
          requests in production, so there is no automated sync anymore.
          Read-only coverage report below the upload controls; existing
          consumers are untouched by this page. */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" rowGap={0.5} sx={{ mb: 1 }}>
            <Typography variant="h6">Sector/Industry Classification</Typography>
            <LastRunSummary
              loading={sectorStatusLoading}
              status={sectorStatus?.latest?.status ?? null}
              timestamp={sectorStatus?.latest?.completed_at}
            />
            {sectorStatus?.latest?.summary?.triggered_by && (
              <Typography variant="caption" color="text.secondary">
                (manual)
              </Typography>
            )}
          </Stack>
          <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
            Upload an NSE Indices index-constituent CSV (e.g. from{" "}
            <code>https://www.niftyindices.com/IndexConstituent/ind_nifty500list.csv</code> — download it
            yourself in a browser, then upload it here) to populate <code>sector</code>/<code>industry</code>{" "}
            for every matching stock (ISIN match first, symbol fallback) — the single source of truth the
            Breakout Scanner, RSI2 Pullback, and Stock Data page all already read from; nothing on those
            pages needs to change when this runs. There is no automated sync — niftyindices.com blocks
            non-browser requests from this server.
          </Alert>

          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" rowGap={2}>
            <Button variant="outlined" component="label" htmlFor="sector-classification-file">
              Choose CSV file
              <input
                id="sector-classification-file"
                ref={sectorFileInputRef}
                type="file"
                accept=".csv"
                hidden
                onChange={(e) => setSectorFile(e.target.files?.[0] ?? null)}
              />
            </Button>
            {sectorFile && (
              <Typography variant="body2" color="text.secondary">
                {sectorFile.name}
              </Typography>
            )}
            <Button
              variant="contained"
              startIcon={sectorUploading ? <CircularProgress size={16} color="inherit" /> : <CategoryIcon />}
              onClick={handleSectorUpload}
              disabled={!sectorFile || sectorUploading}
            >
              {sectorUploading ? "Uploading…" : "Upload"}
            </Button>
          </Stack>

          {sectorUploadError && (
            <Alert severity="error" sx={{ mt: 2 }} onClose={() => setSectorUploadError(null)}>
              {sectorUploadError}
            </Alert>
          )}
          {sectorUploadResult && (
            <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
              <Typography variant="body2">
                {sectorUploadResult.total_rows_processed} row(s) read — {sectorUploadResult.total_matched}{" "}
                matched ({sectorUploadResult.matched_by_isin} by ISIN, {sectorUploadResult.matched_by_symbol} by
                symbol), {sectorUploadResult.unmapped_symbols} unmapped.
              </Typography>
            </Paper>
          )}
          {sectorCoverageError && (
            <Alert severity="error" sx={{ mt: 2 }} onClose={() => setSectorCoverageError(null)}>
              {sectorCoverageError}
            </Alert>
          )}

          {sectorCoverageLoading ? (
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 2 }}>
              <CircularProgress size={14} />
              <Typography variant="caption" color="text.secondary">
                Loading coverage…
              </Typography>
            </Stack>
          ) : (
            sectorCoverage && (
              <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
                <Stack spacing={1.5}>
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={2.4}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Total Stocks
                      </Typography>
                      <Typography variant="subtitle1" fontWeight={700}>
                        {sectorCoverage.total_stocks}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={2.4}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Classified
                      </Typography>
                      <Typography variant="subtitle1" fontWeight={700}>
                        {sectorCoverage.classified_stocks}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={2.4}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Unclassified
                      </Typography>
                      <Typography variant="subtitle1" fontWeight={700}>
                        {sectorCoverage.unclassified_stocks}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={2.4}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Duplicate Mappings
                      </Typography>
                      <Typography variant="subtitle1" fontWeight={700}>
                        {sectorCoverage.duplicate_mappings}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={2.4}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Unmapped Symbols
                      </Typography>
                      <Typography variant="subtitle1" fontWeight={700}>
                        {sectorCoverage.unmapped_symbols}
                      </Typography>
                    </Grid>
                  </Grid>
                  <Stack direction="row" spacing={0.75} alignItems="flex-start">
                    <Tooltip title={sectorCoverage.hierarchy_note} arrow placement="top">
                      <InfoOutlinedIcon fontSize="small" color="action" sx={{ mt: "1px" }} />
                    </Tooltip>
                    <Typography variant="caption" color="text.secondary">
                      {sectorCoverage.hierarchy_note}
                    </Typography>
                  </Stack>
                </Stack>
              </Paper>
            )
          )}
        </CardContent>
      </Card>

      {/* Volume + OBV Breakout Scanner: readiness, run, backfill (added 2026-10-04). */}
      <VolumeObvScannerOperationsCard />

      {/* Data Health & Execution Integrity (read-only; added 2026-10-17). */}
      <ExecutionIntegrityCard />
    </Box>
  );
}

export default DataOperationsPage;
